import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Switch,
  FormControlLabel,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Widgets as WidgetsIcon
} from '@mui/icons-material';
import { useCamposDinamicos } from '../../../context/CamposDinamicosContext';

/**
 * Componente para gestión de campos globales
 * Solo para Admin del Sistema
 * Permite crear campos que pueden ser reutilizados por todas las empresas
 */
const GestionCamposGlobales = () => {
  const {
    catalogoCampos,
    loadingCatalogo,
    cargarCatalogo,
    crearCampo,
    actualizarCampo,
    eliminarCampo
  } = useCamposDinamicos();

  const [mensaje, setMensaje] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [campoSeleccionado, setCampoSeleccionado] = useState(null);
  const [procesando, setProcesando] = useState(false);

  // Tipos de campo disponibles
  const tiposCampo = [
    { value: 'texto', label: 'Texto corto' },
    { value: 'textarea', label: 'Texto largo (Textarea)' },
    { value: 'numero', label: 'Número' },
    { value: 'fecha', label: 'Fecha' },
    { value: 'boolean', label: 'Sí/No (Boolean)' },
    { value: 'email', label: 'Email' },
    { value: 'telefono', label: 'Teléfono' },
    { value: 'url', label: 'URL' },
    { value: 'select', label: 'Lista de opciones (Select)' },
    { value: 'archivo', label: 'Archivo adjunto' }
  ];

  // Estado del formulario
  const [formData, setFormData] = useState({
    nombre_campo: '',
    etiqueta_display: '',
    tipo_campo: 'texto',
    descripcion: '',
    configuracion_campo: {},
    validaciones: {},
    es_global: true
  });

  // Estado para opciones de select
  const [opciones, setOpciones] = useState('');

  // Cargar catálogo al montar
  useEffect(() => {
    cargarCatalogo({ solo_globales: true });
  }, []);

  /**
   * Limpiar formulario
   */
  const limpiarFormulario = () => {
    setFormData({
      nombre_campo: '',
      etiqueta_display: '',
      tipo_campo: 'texto',
      descripcion: '',
      configuracion_campo: {},
      validaciones: {},
      es_global: true
    });
    setOpciones('');
    setModoEdicion(false);
    setCampoSeleccionado(null);
  };

  /**
   * Abrir dialog para crear
   */
  const handleNuevoCampo = () => {
    limpiarFormulario();
    setOpenDialog(true);
  };

  /**
   * Abrir dialog para editar
   */
  const handleEditarCampo = (campo) => {
    setCampoSeleccionado(campo);
    setFormData({
      nombre_campo: campo.nombre_campo,
      etiqueta_display: campo.etiqueta_display,
      tipo_campo: campo.tipo_campo,
      descripcion: campo.descripcion || '',
      configuracion_campo: campo.configuracion_campo || {},
      validaciones: campo.validaciones || {},
      es_global: campo.es_global
    });

    // Si es select, cargar opciones
    if (campo.tipo_campo === 'select' && campo.configuracion_campo?.opciones) {
      setOpciones(campo.configuracion_campo.opciones.join('\n'));
    }

    setModoEdicion(true);
    setOpenDialog(true);
  };

  /**
   * Guardar campo (crear o actualizar)
   */
  const handleGuardarCampo = async () => {
    // Validaciones
    if (!formData.nombre_campo.trim()) {
      setMensaje({ type: 'error', text: 'El nombre del campo es obligatorio' });
      return;
    }

    if (!formData.etiqueta_display.trim()) {
      setMensaje({ type: 'error', text: 'La etiqueta es obligatoria' });
      return;
    }

    try {
      setProcesando(true);

      // Procesar configuración según tipo de campo
      const campoData = { ...formData };

      // Si es select, procesar opciones
      if (formData.tipo_campo === 'select' && opciones.trim()) {
        campoData.configuracion_campo = {
          ...campoData.configuracion_campo,
          opciones: opciones.split('\n').map(opt => opt.trim()).filter(opt => opt)
        };
      }

      let result;
      if (modoEdicion && campoSeleccionado) {
        // Actualizar
        result = await actualizarCampo(campoSeleccionado.id_campo, campoData);
      } else {
        // Crear
        result = await crearCampo(campoData);
      }

      if (result.success) {
        setMensaje({
          type: 'success',
          text: modoEdicion ? 'Campo actualizado correctamente' : 'Campo creado correctamente'
        });
        setOpenDialog(false);
        limpiarFormulario();
        await cargarCatalogo({ solo_globales: true });
      } else {
        setMensaje({
          type: 'error',
          text: result.error || 'Error al guardar campo'
        });
      }
    } catch (error) {
      console.error('Error guardando campo:', error);
      setMensaje({
        type: 'error',
        text: 'Error inesperado al guardar campo'
      });
    } finally {
      setProcesando(false);
    }
  };

  /**
   * Eliminar campo
   */
  const handleEliminarCampo = async (idCampo, nombreCampo) => {
    if (!window.confirm(`¿Estás seguro de eliminar el campo "${nombreCampo}"?`)) {
      return;
    }

    try {
      const result = await eliminarCampo(idCampo);
      if (result.success) {
        setMensaje({
          type: 'success',
          text: 'Campo eliminado correctamente'
        });
        await cargarCatalogo({ solo_globales: true });
      } else {
        setMensaje({
          type: 'error',
          text: result.error || 'Error al eliminar campo'
        });
      }
    } catch (error) {
      console.error('Error eliminando campo:', error);
      setMensaje({
        type: 'error',
        text: 'Error inesperado al eliminar campo'
      });
    }
  };

  /**
   * Obtener color del chip según tipo de campo
   */
  const getColorTipo = (tipo) => {
    const colores = {
      texto: 'primary',
      textarea: 'primary',
      numero: 'secondary',
      fecha: 'info',
      boolean: 'success',
      email: 'warning',
      telefono: 'warning',
      url: 'warning',
      select: 'error',
      archivo: 'default'
    };
    return colores[tipo] || 'default';
  };

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          <WidgetsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Gestión de Campos Globales
        </Typography>
        <Box>
          <Tooltip title="Recargar">
            <IconButton
              onClick={() => cargarCatalogo({ solo_globales: true })}
              disabled={loadingCatalogo}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleNuevoCampo}
            sx={{ ml: 1 }}
          >
            Nuevo Campo Global
          </Button>
        </Box>
      </Box>

      {/* Mensajes */}
      {mensaje && (
        <Alert severity={mensaje.type} onClose={() => setMensaje(null)} sx={{ mb: 3 }}>
          {mensaje.text}
        </Alert>
      )}

      {/* Tabla de campos */}
      {loadingCatalogo ? (
        <Box display="flex" justifyContent="center" py={5}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={3}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>ID</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Nombre Técnico</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Etiqueta</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Tipo</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Descripción</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">
                  Estado
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {catalogoCampos.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="textSecondary" py={3}>
                      No hay campos globales creados
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                catalogoCampos
                  .filter((campo) => campo.es_global)
                  .map((campo) => (
                    <TableRow key={campo.id_campo} hover>
                      <TableCell>{campo.id_campo}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {campo.nombre_campo}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" fontWeight="bold">
                          {campo.etiqueta_display}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={campo.tipo_campo}
                          size="small"
                          color={getColorTipo(campo.tipo_campo)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {campo.descripcion || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={campo.is_activo ? 'Activo' : 'Inactivo'}
                          size="small"
                          color={campo.is_activo ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEditarCampo(campo)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() =>
                              handleEliminarCampo(campo.id_campo, campo.etiqueta_display)
                            }
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog: Crear/Editar Campo */}
      <Dialog
        open={openDialog}
        onClose={() => !procesando && setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight="bold">
            {modoEdicion ? 'Editar Campo Global' : 'Crear Nuevo Campo Global'}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {/* Nombre técnico */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nombre Técnico"
                value={formData.nombre_campo}
                onChange={(e) =>
                  setFormData({ ...formData, nombre_campo: e.target.value })
                }
                required
                helperText="Snake case. Ej: temperatura_cliente"
                disabled={modoEdicion}
              />
            </Grid>

            {/* Etiqueta */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Etiqueta Display"
                value={formData.etiqueta_display}
                onChange={(e) =>
                  setFormData({ ...formData, etiqueta_display: e.target.value })
                }
                required
                helperText="Texto que verá el usuario"
              />
            </Grid>

            {/* Tipo de campo */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Tipo de Campo</InputLabel>
                <Select
                  value={formData.tipo_campo}
                  onChange={(e) =>
                    setFormData({ ...formData, tipo_campo: e.target.value })
                  }
                  label="Tipo de Campo"
                >
                  {tiposCampo.map((tipo) => (
                    <MenuItem key={tipo.value} value={tipo.value}>
                      {tipo.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Global */}
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.es_global}
                    onChange={(e) =>
                      setFormData({ ...formData, es_global: e.target.checked })
                    }
                    color="primary"
                  />
                }
                label="Campo Global (compartido entre empresas)"
              />
            </Grid>

            {/* Descripción */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción"
                multiline
                rows={2}
                value={formData.descripcion}
                onChange={(e) =>
                  setFormData({ ...formData, descripcion: e.target.value })
                }
                helperText="Descripción del campo para documentación"
              />
            </Grid>

            {/* Opciones (solo para tipo select) */}
            {formData.tipo_campo === 'select' && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }}>
                    <Chip label="Configuración de Select" size="small" />
                  </Divider>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Opciones (una por línea)"
                    multiline
                    rows={4}
                    value={opciones}
                    onChange={(e) => setOpciones(e.target.value)}
                    helperText="Ingresa cada opción en una línea separada"
                    placeholder={'Opción 1\nOpción 2\nOpción 3'}
                  />
                </Grid>
              </>
            )}

            {/* Validaciones */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>
                <Chip label="Validaciones" size="small" />
              </Divider>
            </Grid>

            {/* Campo requerido */}
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.validaciones?.required || false}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        validaciones: {
                          ...formData.validaciones,
                          required: e.target.checked
                        }
                      })
                    }
                    color="primary"
                  />
                }
                label="Campo Requerido"
              />
            </Grid>

            {/* Validaciones específicas por tipo */}
            {(formData.tipo_campo === 'texto' || formData.tipo_campo === 'textarea') && (
              <>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Longitud Mínima"
                    value={formData.validaciones?.minLength || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        validaciones: {
                          ...formData.validaciones,
                          minLength: parseInt(e.target.value) || undefined
                        }
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Longitud Máxima"
                    value={formData.validaciones?.maxLength || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        validaciones: {
                          ...formData.validaciones,
                          maxLength: parseInt(e.target.value) || undefined
                        }
                      })
                    }
                  />
                </Grid>
              </>
            )}

            {formData.tipo_campo === 'numero' && (
              <>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Valor Mínimo"
                    value={formData.validaciones?.min || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        validaciones: {
                          ...formData.validaciones,
                          min: parseFloat(e.target.value) || undefined
                        }
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Valor Máximo"
                    value={formData.validaciones?.max || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        validaciones: {
                          ...formData.validaciones,
                          max: parseFloat(e.target.value) || undefined
                        }
                      })
                    }
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} disabled={procesando}>
            Cancelar
          </Button>
          <Button
            onClick={handleGuardarCampo}
            variant="contained"
            color="primary"
            disabled={procesando}
            startIcon={procesando ? <CircularProgress size={20} /> : null}
          >
            {procesando ? 'Guardando...' : modoEdicion ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GestionCamposGlobales;
